import { useMemo } from 'react';

import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { ClientEnvironment } from '@polyai/common/constants/global.constants';
import useRequiredParams from '@polyai/common/hooks/useRequiredParams';

import api from 'api';
import type {
  CountryCode,
  PhoneNumberType,
} from 'api/resources/phoneNumber/types';
import { useToast } from 'hooks/useToast';
import { useIntegrations } from 'screens/Integrations/hooks/useIntegrations';
import { useIsPlgProd } from './useIsPlgProd';
import useSampleProject from './useSampleProjects/useSampleProject';

export const PHONE_NUMBER_MUTATION_KEY = 'phone-number';
export const ALL_ENVIRONMENTS_QUERY_KEY = 'all-environments';

// TODO: Change how fetching number per client environment is done
// once we enable purchasing numbers per client environment
const usePhoneNumber = (clientEnv?: ClientEnvironment) => {
  const { accountId, projectId } = useRequiredParams<{
    accountId: string;
    projectId: string;
  }>();
  const { inSampleProject } = useSampleProject();
  const isPhoneNumbersDisabled = useIsPlgProd();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { activeTelephonyIntegration } = useIntegrations();

  const queryKey = [accountId, projectId, clientEnv, PHONE_NUMBER_MUTATION_KEY];
  const allPhoneNumbersQueryKey = [
    accountId,
    projectId,
    PHONE_NUMBER_MUTATION_KEY,
    ALL_ENVIRONMENTS_QUERY_KEY,
    activeTelephonyIntegration?.id,
  ];

  const {
    data,
    isLoading: isLoadingPhoneNumber,
    isError: isPhoneNumberError,
  } = useQuery({
    queryKey,
    queryFn: () => api.getPhoneNumber(accountId, projectId, clientEnv),
    enabled: !inSampleProject && !isPhoneNumbersDisabled,
  });

  const { data: allPhoneNumbers, isLoading: isLoadingPhoneNumbers } = useQuery({
    queryKey: allPhoneNumbersQueryKey,
    queryFn: () => api.getPhoneNumbersInAllEnvironments(accountId, projectId),
    enabled: !isPhoneNumbersDisabled,
  });

  const mutationKey = [accountId, projectId, PHONE_NUMBER_MUTATION_KEY];

  const { mutate: _buyPhoneNumberMutate, mutateAsync: buyPhoneNumberAsync } =
    useMutation(
      (params: {
        countryCode: CountryCode;
        clientEnv: ClientEnvironment;
        variantId?: string;
        areaCode?: string;
      }) => {
        return api.buyPhoneNumber({
          accountId,
          projectId,
          countryCode: params.countryCode,
          clientEnv: params.clientEnv,
          ...(params.variantId && { variantId: params.variantId }),
          ...(params.areaCode && { areaCode: params.areaCode }),
        });
      },
      {
        mutationKey,
        onSuccess: () => {
          queryClient
            .invalidateQueries(queryKey)
            .catch(e =>
              console.error('Error occurred. Could not invalidate queries', e),
            );
          queryClient
            .invalidateQueries(allPhoneNumbersQueryKey)
            .catch(e =>
              console.error('Error occurred. Could not invalidate queries', e),
            );
          toast.success({
            title: `Number added`,
          });
        },
      },
    );

  const buyPhoneNumber = (params: {
    countryCode: CountryCode;
    clientEnv: ClientEnvironment;
    variantId?: string;
    areaCode?: string;
  }) => {
    _buyPhoneNumberMutate(params, {
      onError: (error: any) =>
        toast.error({
          title: `Failed to add number`,
          description:
            error?.message ||
            `Something went wrong. Please wait a few seconds and then try again.`,
        }),
    });
  };

  const isMutating = useIsMutating({ mutationKey });
  const isReassigningVariant = useIsMutating({
    mutationKey: [...mutationKey, 'reassign'],
  });

  const { sandBoxNumbers, preReleasePhoneNumbers, livePhoneNumbers } =
    useMemo(() => {
      const result: Record<string, PhoneNumberType[]> = {
        sandBoxNumbers: [],
        preReleasePhoneNumbers: [],
        livePhoneNumbers: [],
      };
      allPhoneNumbers?.phone_numbers?.forEach(item => {
        const arrayToPushIn =
          item.client_env === ClientEnvironment.SANDBOX
            ? result.sandBoxNumbers
            : item.client_env === ClientEnvironment.PRE_RELEASE
              ? result.preReleasePhoneNumbers
              : result.livePhoneNumbers;

        arrayToPushIn.push(item);
      });

      return result;
    }, [allPhoneNumbers]);

  const { mutate: deletePhoneNumber } = useMutation(
    (params: {
      phoneNumber: string;
      clientEnv: ClientEnvironment;
      integration?: string;
    }) => api.deletePhoneNumber({ accountId, projectId, ...params }),
    {
      mutationKey,
      onSuccess: () => {
        queryClient
          .invalidateQueries(queryKey)
          .catch(e =>
            console.error('Error occurred. Could not invalidate queries', e),
          );
        queryClient
          .invalidateQueries(allPhoneNumbersQueryKey)
          .catch(e =>
            console.error('Error occurred. Could not invalidate queries', e),
          );
        toast.success({
          title: `Number deleted`,
        });
      },
      onError: () =>
        toast.error({
          title: `Failed to delete phone number`,
        }),
    },
  );

  const { mutate: reassignPhoneNumberVariant } = useMutation(
    // TODO: Remove phoneNumber parameter once we remove the old reassign phone number variant API
    (params: {
      connectorId?: string;
      phoneNumber?: string;
      variantId: string;
    }) => api.reassignPhoneNumberVariant({ accountId, projectId, ...params }),
    {
      mutationKey: [...mutationKey, 'reassign'],
      onSuccess: () => {
        queryClient
          .invalidateQueries(queryKey)
          .catch(e =>
            console.error('Error occurred. Could not invalidate queries', e),
          );
        queryClient
          .invalidateQueries(allPhoneNumbersQueryKey)
          .catch(e =>
            console.error('Error occurred. Could not invalidate queries', e),
          );
        toast.success({
          title: `Variant added`,
        });
      },
      onError: () =>
        toast.error({
          title: `Failed to reassign variant`,
        }),
    },
  );

  return {
    phoneNumber: data?.phone_number,
    isLoadingPhoneNumber,
    isPhoneNumberError,
    buyPhoneNumber,
    buyPhoneNumberAsync,
    sandBoxNumbers,
    preReleasePhoneNumbers,
    livePhoneNumbers,
    isLoadingPhoneNumbers,
    isBuyingPhoneNumber: !!isMutating,
    isReassigningVariant: !!isReassigningVariant,
    deletePhoneNumber,
    reassignPhoneNumberVariant,
  };
};

export default usePhoneNumber;
